import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { canManagePurchaseOrders, canReceivePurchaseOrders } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { ActionMenu, type ActionMenuItem } from "@/components/base/ActionMenu";
import { money, fmtDate, fmtDateTime, variantLabel } from "@/lib/format";
import * as purchasingApi from "@/lib/api/purchasing";
import { poStatusLabel, canApprovePo, canReceivePo, canCancelPo } from "@/lib/purchasing";
import { ScanReceivePanel, type ScanReceiveLine, type ReceiveLine } from "@/components/feature/ScanReceivePanel";
import EditDraftModal from "./components/EditDraftModal";
import { firstRelation } from "@/lib/relations";

interface PoRow {
  id: string;
  supplier_id: string | null;
  location_id: string | null;
  po_number: string;
  status: string;
  order_date: string | null;
  expected_date: string | null;
  total: number;
  currency: string;
  notes: string | null;
  over_receipt_policy: string | null;
  supplier_reference: string | null;
  shipping_cost: number | null;
  discount: number | null;
  suppliers?: { name: string } | null;
  locations?: { name: string } | null;
}

interface PoItemRow extends CatalogueHistoryFields {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_ordered: number;
  qty_received: number;
  unit_cost: number;
  total: number;
  supplier_sku: string | null;
  products?: { name: string; sku: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface ReceiptRow {
  id: string;
  receipt_number: string;
  received_at: string | null;
  notes: string | null;
  locations?: { name: string } | null;
}

interface ReceiptItemRow extends CatalogueHistoryFields {
  id: string;
  receipt_id: string;
  product_id: string | null;
  qty_received: number;
  products?: { name: string; sku: string } | null;
}

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [receiveIdemKey, setReceiveIdemKey] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["po-detail", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [po, items, receipts] = await Promise.all([
        supabase
          .from("purchase_orders")
          .select("id, supplier_id, location_id, po_number, status, order_date, expected_date, total, currency, notes, over_receipt_policy, supplier_reference, shipping_cost, discount, suppliers(name), locations(name)")
          .eq("org_id", org!.id)
          .eq("id", id)
          .maybeSingle(),
        supabase
          .from("purchase_order_items")
          .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, product_id, variant_id, qty_ordered, qty_received, unit_cost, total, supplier_sku, products(name, sku), variants(sku, option_values)")
          .eq("org_id", org!.id)
          .eq("purchase_order_id", id)
          .order("created_at"),
        supabase
          .from("purchase_receipts")
          .select("id, receipt_number, received_at, notes, locations(name)")
          .eq("org_id", org!.id)
          .eq("purchase_order_id", id)
          .order("received_at", { ascending: false }),
      ]);
      const receiptIds = (receipts.data ?? []).map((r) => r.id);
      const receiptItems =
        receiptIds.length === 0
          ? { data: [] }
          : await supabase
              .from("purchase_receipt_items")
              .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, receipt_id, product_id, qty_received, products(name, sku)")
              .eq("org_id", org!.id)
              .in("receipt_id", receiptIds)
              .order("created_at");
      return {
        po: po.data
          ? {
              ...po.data,
              suppliers: firstRelation(po.data.suppliers),
              locations: firstRelation(po.data.locations),
            } as PoRow
          : null,
        items: (items.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as PoItemRow[],
        receipts: (receipts.data ?? []).map((row) => ({
          ...row,
          locations: firstRelation(row.locations),
        })) as ReceiptRow[],
        receiptItems: (receiptItems.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
        })) as ReceiptItemRow[],
      };
    },
  });

  const scanLines: ScanReceiveLine[] = (data?.items ?? []).filter(hasLiveCatalogueIdentity).map((it) => ({
    id: it.id,
    product_id: it.product_id,
    variant_id: it.variant_id,
    name: historicalProductName(it),
    sku: historicalSku(it),
    outstanding: it.qty_ordered - it.qty_received,
  }));

  const openReceive = () => {
    setError("");
    if ((data?.items ?? []).some((it) => (it.qty_ordered - it.qty_received) > 0 && !hasLiveCatalogueIdentity(it))) {
      setError("A pending line has no live catalogue identity. Reconcile it before receiving stock.");
      return;
    }
    setReceiveIdemKey(crypto.randomUUID());
    setReceiveOpen(true);
  };

  const commitReceiveLines = async (pLines: ReceiveLine[]) => {
    await purchasingApi.receivePurchaseOrder(id!, pLines, receiveIdemKey || undefined);
  };

  const onReceiveCommitted = () => {
    setReceiveOpen(false);
    queryClient.invalidateQueries({ queryKey: ["po-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleCancel = async () => {
    setError("");
    setSaving(true);
    try {
      await purchasingApi.cancelPurchaseOrder(id!);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      setCancelOpen(false);
      return;
    }
    setSaving(false);
    setCancelOpen(false);
    queryClient.invalidateQueries({ queryKey: ["po-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleApprove = async () => {
    setError("");
    setSaving(true);
    try {
      await purchasingApi.approvePurchaseOrder(id!);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    // Keep the detail screen synchronized with the authoritative lifecycle
    // transition before exposing the next available action.
    await queryClient.invalidateQueries({
      queryKey: ["po-detail", org?.id, id],
    });

    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.po) {
    return (
      <div>
        <PageHeader title="Purchase Order" backTo="/purchase-orders" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this purchase order.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const po = data.po;
  const receivedCount = data.items.reduce((s, it) => s + it.qty_received, 0);
  const orderedCount = data.items.reduce((s, it) => s + it.qty_ordered, 0);
  const canReceive = canReceivePo(po.status) && canReceivePurchaseOrders(role);
  const canCancel = canCancelPo(po.status) && canManagePurchaseOrders(role);
  const canApprove = canApprovePo(po.status) && canManagePurchaseOrders(role);
  const canEdit = po.status === "draft" && canManagePurchaseOrders(role);

  const poMenuItems: ActionMenuItem[] = [
    ...(canCancel ? [{ key: "cancel", label: "Cancel order", icon: "ri-close-circle-line", onClick: () => setCancelOpen(true), destructive: true }] : []),
  ];

  return (
    <div>
      <PageHeader title={po.po_number} subtitle={po.suppliers?.name ?? "—"} backTo="/purchase-orders">
        {canEdit && (
          <button
            onClick={() => { setError(""); setEditOpen(true); }}
            className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-edit-line" /> Edit Draft
          </button>
        )}
        {canApprove && (
          <button
            onClick={handleApprove}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-check-line" /> Mark as ordered
          </button>
        )}
        {canReceive && (
          <button
            onClick={openReceive}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-download-2-line" /> Receive Stock
          </button>
        )}
        {poMenuItems.length > 0 && <ActionMenu label="More" items={poMenuItems} />}
      </PageHeader>

      {error && (
        <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={po.status} customLabel={poStatusLabel(po.status)} />
                <span className="text-sm text-slate-500">Ordered {fmtDate(po.order_date)}</span>
                <span className="text-sm text-slate-500">Expected {fmtDate(po.expected_date)}</span>
                {po.over_receipt_policy && po.over_receipt_policy !== "strict" && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium capitalize">
                    {po.over_receipt_policy?.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{money(po.total, po.currency)}</p>
                {po.currency && <p className="text-xs text-slate-400 mt-0.5">{po.currency.toUpperCase()}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-400">Supplier</p>
                {po.supplier_id ? (
                  <Link to={`/suppliers/${po.supplier_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700">{po.suppliers?.name ?? "—"}</Link>
                ) : (
                  <p className="text-sm font-medium text-slate-800">{po.suppliers?.name ?? "—"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400">Receiving location</p>
                {po.location_id ? (
                  <Link to={`/locations/${po.location_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700">{po.locations?.name ?? "—"}</Link>
                ) : (
                  <p className="text-sm font-medium text-slate-800">{po.locations?.name ?? "—"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400">Progress</p>
                <p className="text-sm font-medium text-slate-800">
                  {receivedCount} / {orderedCount} received
                </p>
                {orderedCount - receivedCount > 0 && (
                  <p className="text-xs font-medium text-amber-600 mt-0.5">{orderedCount - receivedCount} outstanding</p>
                )}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {po.supplier_reference && (
                <div>
                  <p className="text-xs text-slate-400">Supplier reference</p>
                  <p className="text-sm font-medium text-slate-800">{po.supplier_reference}</p>
                </div>
              )}
              {po.shipping_cost != null && Number(po.shipping_cost) > 0 && (
                <div>
                  <p className="text-xs text-slate-400">Shipping cost</p>
                  <p className="text-sm font-medium text-slate-800">{money(po.shipping_cost, po.currency)}</p>
                </div>
              )}
              {po.discount != null && Number(po.discount) > 0 && (
                <div>
                  <p className="text-xs text-slate-400">Discount</p>
                  <p className="text-sm font-medium text-slate-800">{money(po.discount, po.currency)}</p>
                </div>
              )}
            </div>
            {po.notes && <p className="text-sm text-slate-600 mt-4">{po.notes}</p>}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800">Line Items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ordered</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Received</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit cost</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={it.id} className="border-b border-slate-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-800">
                          {it.product_id ? <Link to={`/products/${it.product_id}`} className="hover:text-emerald-700">{historicalProductName(it)}</Link> : <span>{historicalProductName(it)}</span>}
                          {(variantLabel(historicalVariantOptions(it)) || historicalSku(it)) && (
                            <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(it)) || historicalSku(it)}</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400 font-mono">{historicalSku(it)}</p>
                        {it.supplier_sku && <p className="text-xs text-slate-400 font-mono">Supplier SKU: {it.supplier_sku}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{it.qty_ordered}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{it.qty_received}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{money(it.unit_cost, po.currency)}</td>
                      <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{money(it.total, po.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden h-fit">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Receipt History</h3>
            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{data.receipts.length}</span>
          </div>
          {data.receipts.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">No receipts yet</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {data.receipts.map((rc) => (
                <div key={rc.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800 font-mono">{rc.receipt_number}</span>
                    <span className="text-xs text-slate-400">{fmtDateTime(rc.received_at)}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{rc.locations?.name ?? "—"}</p>
                  {rc.notes && <p className="text-xs text-slate-500 mt-1">{rc.notes}</p>}
                  <div className="mt-2 space-y-1">
                    {data.receiptItems
                      .filter((ri) => ri.receipt_id === rc.id)
                      .map((ri) => (
                        <div key={ri.id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">{historicalProductName(ri)}</span>
                          <span className="text-emerald-700 font-medium">+{ri.qty_received}</span>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel Purchase Order"
        message={`Cancel ${po.po_number}? Any unreceived quantities will no longer count as incoming stock. This cannot be undone.`}
        confirmLabel="Cancel Order"
        loading={saving}
        onCancel={() => setCancelOpen(false)}
        onConfirm={handleCancel}
      />

      <Modal
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        title={`Receive — ${po.po_number}`}
        footer={
          <button onClick={() => setReceiveOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
            Close
          </button>
        }
      >
        <ScanReceivePanel
          docType="purchase_order"
          docId={id ?? ""}
          orgId={org!.id}
          lines={scanLines}
          commitFn={commitReceiveLines}
          onCommitted={onReceiveCommitted}
        />
      </Modal>

      <EditDraftModal
        open={editOpen}
        po={po}
        items={data.items}
        orgId={org!.id}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          queryClient.invalidateQueries({ queryKey: ["po-detail", org?.id, id] });
          queryClient.invalidateQueries({ queryKey: ["purchase-orders", org?.id] });
          queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
          queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
        }}
      />
    </div>
  );
}